import React from "react"
import {
  createHashRouter,   // ⬅️ use hash router to support deep links without server rewrites
  redirect,
  RouterProvider,
} from "react-router-dom"
import AppErrorBoundary from "./components/AppErrorBoundary"
import { GlobalDevShortcuts } from "./components/GlobalDevShortcuts"

import NotFoundPage from "./pages/NotFoundPage"
import SARatesPage from "./pages/super-admin/Screens/RatesPage"
import LoginPage from "./pages/auth/LoginPage"
import RegisterPage from "./pages/auth/RegisterPage"

// HOA-Admin imports
import HoaAdminLayout from "./pages/hoa-admin/layout/HoaAdminLayout"
import DashboardPage from "./pages/hoa-admin/Screens/DashboardPage"
import IssuesPage from "./pages/hoa-admin/Screens/IssuesPage"
import PaymentsPage from "./pages/hoa-admin/Screens/PaymentsPage"
import SettingsPage from "./pages/hoa-admin/Screens/SettingsPage"
import DevLogsPage from "./pages/hoa-admin/Screens/DevLogsPage"
import ReportsPage from "./pages/hoa-admin/Screens/ReportsPage"
import CommunicationsPage from "./pages/hoa-admin/Screens/CommunicationsPage"
import MemberManagementPage from "./pages/hoa-admin/Screens/MemberManagementPage"
import BuildingDetailPage from "./pages/hoa-admin/Screens/BuildingDetailPage"

// Optional lazy wrapper to keep router snippet tidy (or just use CommunicationsPage directly)
const CommunicationsLazy = React.lazy(async () => ({ default: CommunicationsPage }))


// ✅ Resident routes
import { residentRoutes } from "./pages/resident/routes"

// 🔐 Super Admin imports (real screens + layout)
import SuperAdminLayout from "./pages/super-admin/layout/SuperAdminLayout"
import SADashboardPage from "./pages/super-admin/Screens/DashboardPage"
import SAHoaAdminsPage from "./pages/super-admin/Screens/HoaAdminsPage"
import SAPaymentsInboxPage from "./pages/super-admin/Screens/PaymentsInboxPage"
import SAPaymentDetailsPage from "./pages/super-admin/Screens/PaymentDetailPage"
import SASettingsPage from "./pages/super-admin/Screens/SettingsPage"

/** ----------------------------------------------------------------
 *  Auth / Role helpers
 * ----------------------------------------------------------------*/
export type Role = "SUPER_ADMIN" | "HOA_ADMIN" | "RESIDENT" | null

function getToken(): string | null {
  return sessionStorage.getItem("access_token")
}

function getRole(): Role {
  const raw = sessionStorage.getItem("role")
  if (raw === "SUPER_ADMIN" || raw === "HOA_ADMIN" || raw === "RESIDENT") {
    return raw
  }
  return null
}

/** Require auth via loader */
function requireAuthLoader() {
  const token = getToken()
  if (!token) throw redirect("/login")
  return null
}

/** Require a specific role via loader */
function requireRoleLoader(required: Exclude<Role, null>) {
  return () => {
    const token = getToken()
    const role = getRole()
    if (!token) throw redirect("/login")
    if (role !== required) {
      switch (role) {
        case "SUPER_ADMIN":
          throw redirect("/super-admin")
        case "HOA_ADMIN":
          throw redirect("/hoa-admin")
        case "RESIDENT":
          throw redirect("/resident")
        default:
          throw redirect("/login")
      }
    }
    return null
  }
}

/** Root redirect based on role */
async function rootRedirectLoader() {
  const token = getToken()
  const role = getRole()
  if (!token || !role) throw redirect("/login")

  if (role === "RESIDENT") throw redirect("/resident")
  
  switch (role) {
    case "SUPER_ADMIN":
      throw redirect("/super-admin")
    case "HOA_ADMIN":
      throw redirect("/hoa-admin")
    default:
      throw redirect("/login")
  }
}

/** ----------------------------------------------------------------
 *  TEMP placeholder pages
 * ----------------------------------------------------------------*/
const Page: React.FC<{ title: string; children?: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className="min-h-screen">
    <header className="sticky top-0 z-10 border-b border-[--color-border] bg-white/80 backdrop-blur">
      <div className="page-container flex items-center justify-between py-4">
        <div className="flex items-center gap-2">
          <div className="size-8 rounded-xl" style={{ background: "var(--color-primary)" }} />
          <span className="text-lg font-semibold tracking-tight">Urbe360</span>
        </div>
        <div className="text-sm text-[--color-muted]">{title}</div>
      </div>
    </header>
    <main className="page-container py-8">
      <div className="card p-6">
        <h1 className="mb-2 text-xl font-semibold">{title}</h1>
        <p className="text-sm text-[--color-muted]">Replace this with the real page soon.</p>
        {children}
      </div>
    </main>
  </div>
)

/** ----------------------------------------------------------------
 *  Router
 * ----------------------------------------------------------------*/
// Resident public pages
import ResidentOtpVerifyPage from "./pages/resident/ResidentOtpVerifyPage"
import ResidentSetupPage from "./pages/resident/ResidentSetupPage"
import ResidentPropertySelectionPage from "./pages/resident/ResidentPropertySelectionPage"
import ResidentPasswordResetPage from "./pages/resident/ResidentPasswordResetPage"
import AdminLoginPage from "./pages/auth/AdminLoginPage"
import LogoutPage from "./pages/auth/LogoutPage"
import ClearStoragePage from "./pages/auth/ClearStoragePage"

export const router = createHashRouter([
  // Public
  { path: "/login", element: <LoginPage />, errorElement: <AppErrorBoundary /> },
  { path: "/register", element: <RegisterPage />, errorElement: <AppErrorBoundary /> },
  { path: "/resident-otp-verify", element: <ResidentOtpVerifyPage /> },
  // Backwards compatibility redirect for old route
  { path: "/resident-login", loader: () => redirect("/resident-otp-verify"), element: <div /> },
  { path: "/resident/setup", element: <ResidentSetupPage /> },
  { path: "/resident/select-property", element: <ResidentPropertySelectionPage /> },
  { path: "/forgot-password", element: <ResidentPasswordResetPage /> },
  { path: "/resident/password-reset", element: <ResidentPasswordResetPage /> },
  { path: "/admin-login-hidden", element: <AdminLoginPage /> },
  { path: "/logout", element: <LogoutPage /> },
  { path: "/clear-storage", element: <ClearStoragePage /> },

  // Root → redirect based on role
  { path: "/", loader: rootRedirectLoader, element: <div /> },

  // 🔐 SUPER ADMIN (real screens + layout)
  {
    path: "/super-admin",
    loader: requireRoleLoader("SUPER_ADMIN"),
    element: <SuperAdminLayout />,
    errorElement: <AppErrorBoundary />,
    children: [
      { index: true, element: <SADashboardPage /> },
      { path: "hoa-admins", element: <SAHoaAdminsPage /> },
      { path: "payments", element: <SAPaymentsInboxPage /> },
      { path: "rates", element: <SARatesPage /> },
      { path: "settings", element: <SASettingsPage /> },
      { path: "payments/:id", element: <SAPaymentDetailsPage /> },
    ],
  },

  // 🔐 HOA Admin
  {
    path: "/hoa-admin",
    loader: requireRoleLoader("HOA_ADMIN"),
    element: <HoaAdminLayout />,
    errorElement: <AppErrorBoundary />,
    children: [
      { index: true, element: <DashboardPage /> },
      // { path: "announcements", element: <AnnouncementsPage /> },
      {
        path: "communications",
        // default to announcements tab if not provided
        element: <React.Suspense fallback={<div />}>
          <CommunicationsLazy />
        </React.Suspense>,
      },
      // { path: "polling", element: <PollingPage /> },
      { path: "reports", element: <ReportsPage /> },
      // { path: "units", element: <UnitsPage /> },
      // { path: "residents", element: <ResidentsPage /> },
      { path: "issues", element: <IssuesPage /> },
      { path: "payments", element: <PaymentsPage /> },
      { path: "settings", element: <SettingsPage /> },
      // in your router (hoa-admin children)
      { path: "members", element: <MemberManagementPage /> },
      // Building detail route (list is in members?tab=buildings)
      { path: "buildings/:id", element: <BuildingDetailPage /> },

    ],
  },

  // 🔐 RESIDENT routes (uses JWT, not role-based auth)
  {
    ...residentRoutes,
    // No loader - residents authenticate via JWT tokens from OTP login
  },

  // 🔧 DEV TOOLS - Hidden developer portal (no auth required)
  {
    path: "/dev/logs",
    element: <DevLogsPage />,
  },

  // 404
  { path: "*", element: <NotFoundPage />, errorElement: <AppErrorBoundary /> },
])

export function RouterRoot() {
  return <RouterProvider router={router} />
}
